/**
 * AddCardForm - Shared component for adding payment methods
 * Uses Stripe Elements for PCI-compliant card collection
 * Used in: ChangePlanModal, WalletSettings, AddBalanceModal
 *
 * Handles self-service customer creation when no Stripe customer exists:
 * Shows business info form -> creates customer -> proceeds with card addition
 */

import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { CreditCard, Loader2, AlertCircle, CheckCircle, Building2, Mail } from 'lucide-react';

// Initialize Stripe with publishable key from environment
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  is_primary_subscription: boolean;
  is_primary_wallet: boolean;
}

interface AddCardFormProps {
  locationId: string;
  onSuccess: (paymentMethod: PaymentMethod) => void;
  onCancel?: () => void;
  setAsPrimarySubscription?: boolean;
  setAsPrimaryTopup?: boolean;
  showCancelButton?: boolean;
}

// Stripe Elements styling to match Step 11 color scheme
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#374151', // text-gray-700
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      '::placeholder': {
        color: '#9CA3AF', // text-gray-400
      },
      iconColor: '#6B7280', // text-gray-500
    },
    invalid: {
      color: '#DC2626', // text-red-600
      iconColor: '#DC2626',
    },
    complete: {
      color: '#059669', // text-green-600
      iconColor: '#059669',
    },
  },
  hidePostalCode: true,
};

// Business Info Form - shown when no Stripe customer exists
interface BusinessInfoFormProps {
  locationId: string;
  onCustomerCreated: () => void;
  onCancel?: () => void;
  showCancelButton?: boolean;
}

function BusinessInfoForm({
  locationId,
  onCustomerCreated,
  onCancel,
  showCancelButton = true,
}: BusinessInfoFormProps) {
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/create-customer`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            business_name: businessName.trim(),
            email: email.trim(),
          }),
        }
      );

      const data = await response.json() as {
        success: boolean;
        error?: string;
      };

      if (!data.success) {
        throw new Error(data.error || 'Failed to create billing account');
      }

      // Success - notify parent to proceed with card form
      onCustomerCreated();
    } catch (err) {
      console.error('[BusinessInfoForm] Error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = businessName.trim().length > 0 && email.trim().length > 0;

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
        <p className="text-sm text-blue-800">
          <strong>Set up your billing account</strong>
        </p>
        <p className="text-sm text-blue-700 mt-1">
          Please provide your business information to create your billing account before adding a payment method.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Business Name */}
        <div>
          <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-1">
            Business Name
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Building2 className="h-4 w-4 text-gray-400" />
            </div>
            <input
              id="businessName"
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="Your Business Name"
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={loading}
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label htmlFor="billingEmail" className="block text-sm font-medium text-gray-700 mb-1">
            Billing Email
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-4 w-4 text-gray-400" />
            </div>
            <input
              id="billingEmail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="billing@example.com"
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={loading}
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Used for receipts and billing notifications
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex items-start p-3 bg-red-50 border border-red-200 rounded-md">
            <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3">
          {showCancelButton && onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="flex-1 py-2.5 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={loading || !isFormValid}
            className={`${showCancelButton ? 'flex-1' : 'w-full'} py-2.5 px-4 rounded-md text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center ${
              isFormValid
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating Account...
              </>
            ) : (
              'Continue to Payment'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

// Inner form component that uses Stripe hooks
function CardForm({
  locationId,
  onSuccess,
  onCancel,
  setAsPrimarySubscription = true,
  setAsPrimaryTopup = true,
  showCancelButton = true,
}: AddCardFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [success, setSuccess] = useState(false);
  const [needsCustomerSetup, setNeedsCustomerSetup] = useState(false);

  // Check if customer exists on mount by attempting to get a setup intent
  // This provides a better UX by showing the business form immediately if needed
  useEffect(() => {
    const checkCustomerExists = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/billing/${locationId}/setup-intent`,
          {
            method: 'POST',
            credentials: 'include',
          }
        );
        const data = await response.json() as { success: boolean; error?: string };

        if (!data.success && data.error?.includes('No Stripe customer configured')) {
          setNeedsCustomerSetup(true);
        }
      } catch (err) {
        // If we can't check, we'll find out when they try to submit
        console.error('[AddCardForm] Pre-check error:', err);
      }
    };

    checkCustomerExists();
  }, [locationId]);

  const handleCustomerCreated = () => {
    // Customer was created, show the card form
    setNeedsCustomerSetup(false);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setError('Payment form not loaded. Please refresh and try again.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card element not found.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Create SetupIntent on the backend
      const setupResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/setup-intent`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      const setupData = await setupResponse.json() as {
        success: boolean;
        client_secret?: string;
        error?: string;
      };

      if (!setupData.success || !setupData.client_secret) {
        // Check if this is the "no customer" error
        if (setupData.error?.includes('No Stripe customer configured')) {
          setNeedsCustomerSetup(true);
          setLoading(false);
          return;
        }
        throw new Error(setupData.error || 'Failed to create setup intent');
      }

      // 2. Confirm the SetupIntent with Stripe
      const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(
        setupData.client_secret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message || 'Card verification failed');
      }

      if (!setupIntent || setupIntent.status !== 'succeeded') {
        throw new Error('Card setup did not complete successfully');
      }

      // 3. Save the payment method to our backend
      const saveResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/payment-methods`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            payment_method_id: setupIntent.payment_method,
            set_as_primary_subscription: setAsPrimarySubscription,
            set_as_primary_topup: setAsPrimaryTopup,
          }),
        }
      );

      const saveData = await saveResponse.json() as {
        success: boolean;
        payment_method?: PaymentMethod;
        error?: string;
      };

      if (!saveData.success || !saveData.payment_method) {
        throw new Error(saveData.error || 'Failed to save payment method');
      }

      // Success!
      setSuccess(true);
      setTimeout(() => {
        onSuccess(saveData.payment_method!);
      }, 500);
    } catch (err) {
      console.error('[AddCardForm] Error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Show business info form if customer setup is needed
  if (needsCustomerSetup) {
    return (
      <BusinessInfoForm
        locationId={locationId}
        onCustomerCreated={handleCustomerCreated}
        onCancel={onCancel}
        showCancelButton={showCancelButton}
      />
    );
  }

  if (success) {
    return (
      <div className="flex items-center justify-center py-6 text-green-600">
        <CheckCircle className="w-5 h-5 mr-2" />
        <span className="font-medium">Card added successfully!</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Card Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Details
        </label>
        <div className="border border-gray-300 rounded-md p-3 bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all">
          <CardElement
            options={cardElementOptions}
            onChange={(e) => {
              setCardComplete(e.complete);
              if (e.error) {
                setError(e.error.message);
              } else {
                setError(null);
              }
            }}
          />
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Your card is encrypted and securely processed by our payment provider
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-start p-3 bg-red-50 border border-red-200 rounded-md">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Buttons */}
      <div className="flex gap-3">
        {showCancelButton && onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 py-2.5 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={loading || !stripe || !cardComplete}
          className={`${showCancelButton ? 'flex-1' : 'w-full'} py-2.5 px-4 rounded-md text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center ${
            cardComplete
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-gray-400 cursor-not-allowed'
          }`}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Adding Card...
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4 mr-2" />
              Add Card
            </>
          )}
        </button>
      </div>
    </form>
  );
}

// Main export - wraps the form with Stripe Elements provider
export function AddCardForm(props: AddCardFormProps) {
  const [stripeReady, setStripeReady] = useState(false);

  useEffect(() => {
    // Check if Stripe key is configured
    if (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY) {
      setStripeReady(true);
    }
  }, []);

  if (!stripeReady) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-2" />
          <div>
            <p className="text-sm font-medium text-yellow-800">
              Payment system not configured
            </p>
            <p className="text-sm text-yellow-700 mt-1">
              Please contact support to set up payments.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <CardForm {...props} />
    </Elements>
  );
}

// Compact version for inline use (e.g., in dropdowns)
export function AddCardFormCompact(props: AddCardFormProps) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
      <div className="flex items-center mb-3">
        <CreditCard className="w-4 h-4 text-gray-500 mr-2" />
        <span className="text-sm font-medium text-gray-700">Add New Card</span>
      </div>
      <AddCardForm {...props} showCancelButton={true} />
    </div>
  );
}
